import Vue from 'vue';
import vue2Dropzone from 'vue2-dropzone';
import 'vue2-dropzone/dist/vue2Dropzone.min.css';
import '@/assets/stylesheets/v2/_file-uploader.scss';
import { FileUploadInputModel } from '@/helpers/entities/models/FileUploadInputModel';
import ViewportSizeMixin from '@/helpers/mixins/ViewportSizeMixin';
import { CDN_URL } from '@/helpers/constants/app';
export default Vue.extend({
    mixins: [ViewportSizeMixin],
    props: {
        id: {
            type: String,
            default: 'uploader',
        },
        value: {
            type: Array,
            default: () => [],
        },
        centered: {
            type: Boolean,
            default: false,
        },
        hideMessage: {
            type: Boolean,
            default: false,
        },
        noPadding: {
            type: Boolean,
            default: false,
        },
        maxFileSize: {
            type: Number,
            required: false,
            default: 10,
        },
        maxFiles: {
            type: Number,
            required: false,
            default: 0,
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        imageUrl: {
            type: String,
            required: false,
            default: '',
        },
    },
    data() {
        return {
            persistentFiles: [],
            imageLoading: false,
            fileSizeError: false,
        };
    },
    computed: {
        imageSize() {
            const self = this;
            let imageSize = '184';
            if (self.xsOnly) {
                imageSize = '124';
            }
            return imageSize;
        },
        options() {
            const self = this;
            const defaultOpts = {
                url: '/',
                autoQueue: false,
                acceptedFiles: 'image/jpeg, image/png',
                addRemoveLinks: true,
                thumbnailHeight: self.imageSize,
                thumbnailWidth: self.imageSize,
                maxFilesize: self.maxFileSize,
                maxFiles: self.maxFiles,
                init() {
                    this.on('maxfilesexceeded', (file) => {
                        this.removeFile(file); // Remove the file if the max file limit is exceeded
                    });
                },
            };
            return defaultOpts;
        },
    },
    watch: {
        imageUrl(newUrl) {
            if (newUrl) {
                this.clearFiles();
                this.addFileManually(newUrl);
                this.syncModel();
            }
        },
    },
    created() {
        const self = this;
        // Ensure props are loaded via $nextTick() unless we will receive empty
        // array
        self.$nextTick(() => {
            if (self.disabled) {
                self.$refs.uploader.disable();
            }
            else {
                self.$refs.uploader.enable();
            }
            const images = self.value;
            // Sync persistentFiles propery on component mount
            self.persistentFiles = images;
            images.forEach((image) => {
                if (image.url) {
                    self.addFileManually(image.url);
                }
            });
        });
    },
    methods: {
        clearFiles() {
            const self = this;
            const uploader = self.$refs.uploader;
            uploader.removeAllFiles(true);
        },
        syncModel() {
            const self = this;
            self.$emit('input', [...self.persistentFiles, ...self.getAcceptedFiles()]);
        },
        getAcceptedFiles() {
            const self = this;
            const uploader = self.$refs.uploader;
            const files = uploader.getAcceptedFiles();
            return files.map((file) => new FileUploadInputModel(file));
        },
        getRejectedFiles() {
            const self = this;
            const uploader = self.$refs.uploader;
            const files = uploader.getRejectedFiles();
            return files.map((file) => new FileUploadInputModel(file));
        },
        getUploadingFiles() {
            const self = this;
            const uploader = self.$refs.uploader;
            const files = uploader.getUploadingFiles();
            return files.map((file) => new FileUploadInputModel(file));
        },
        /**
         * Get All valid images
         * @see https://rowanwins.github.io/vue-dropzone/docs/dist/#/methods
         */
        onAdd(file) {
            const self = this;
            if (file[0].size / 1000000 > self.maxFileSize) {
                self.fileSizeError = true;
            }
            else {
                self.imageLoading = true;
                self.$emit('img:loading');
            }
        },
        onThumbnailCreate() {
            const self = this;
            self.imageLoading = false;
            self.$emit('img:loaded');
            self.syncModel();
        },
        onRemove(file) {
            const self = this;
            const match = self.persistentFiles.find((image) => {
                return self.getFileName(image.url) === file.name;
            });
            if (match) {
                match.markAsDeleted();
            }
            self.$emit('img:loaded');
            self.$emit('removed');
            self.syncModel();
            if (file.size / 1000000 > self.maxFileSize) {
                self.fileSizeError = false;
            }
            if (self.maxFiles === 1 && self.persistentFiles.length < 1) {
                const uploader = self.$refs.uploader;
                uploader.enable(); // Re-enable dropzone when a file is removed
            }
        },
        /**
         * To display the Object Key for already stored images
         * @author Angelo David <adavid@gorated.ph>
         * @since v0.40.1
         */
        getObjectKey(url) {
            const baseUrl = CDN_URL;
            return url.replace(baseUrl, '');
        },
        matchUrl(url) {
            const regExp = /[\w-]+\.(jpg|png|jpeg)/g;
            return url.match(regExp) || [];
        },
        getFileName(url) {
            const self = this;
            const matches = self.matchUrl(url);
            return matches[0] || self.getObjectKey(url);
        },
        /**
         * To display Base64 Image URL to Dropzone manuallyAddFile Thumbnail
         * @see https://github.com/rowanwins/vue-dropzone/issues/474#issuecomment-522396235
         * @author Angelo David <adavid@gorated.ph>
         * @since v0.40.1
         */
        modifyUrl(url) {
            const self = this;
            const matches = self.matchUrl(url);
            if (matches.length > 0) {
                return url;
            }
            return url.concat('#.jpg');
        },
        /**
         * Manually Add Files into Dropzone
         * @see https://github.com/enyo/dropzone/wiki/FAQ#how-to-show-files-already-stored-on-server
         */
        addFileManually(imageUrl) {
            const self = this;
            const uploader = self.$refs.uploader;
            const fileName = self.getFileName(imageUrl);
            const mockFile = {
                name: fileName,
                size: 12345,
                lastModifiedDate: new Date(),
            };
            uploader.manuallyAddFile(mockFile, self.modifyUrl(imageUrl));
            // if (self.maxFiles === 1 && uploader.files?.length >= 1) {
            //   uploader.disable(); // Disable dropzone when maxFiles is reached
            // }
        },
        onError() {
            const self = this;
            self.loading = false;
        },
        onMaxFilesExceeded() {
            this.$emit('max-files-exceed');
        },
    },
    render(createElement) {
        const self = this;
        return createElement('v-layout', { class: 'column' }, [
            createElement(vue2Dropzone, {
                class: {
                    'file-uploader': true,
                    '-centered': self.centered,
                    '-hide-message': self.hideMessage,
                    '-no-padding': self.noPadding,
                },
                props: {
                    id: self.id,
                    options: self.options,
                    includeStyling: true,
                    destroyDropzone: true,
                    duplicateCheck: true,
                    useCustomSlot: true,
                },
                ref: 'uploader',
                on: {
                    'vdropzone-files-added': self.onAdd,
                    'vdropzone-thumbnail': self.onThumbnailCreate,
                    'vdropzone-drop': self.onError,
                    'vdropzone-removed-file': self.onRemove,
                    'vdropzone-error': self.onError,
                    'vdropzone-max-files-exceeded': self.onMaxFilesExceeded,
                },
            }, self.$slots.default),
            createElement('v-progress-linear', {
                props: {
                    indeterminate: true,
                    height: '5',
                    active: self.imageLoading,
                },
            }),
        ]);
    },
});
